/**
 * ABI encoder/decoder powered by ethers.js.
 * Provides: encodeCall, decodeResult, parseHumanReadableAbi.
 *
 * Wraps ethers to (a) lowercase decoded addresses and (b) flatten ethers' Result
 * objects into plain JS arrays, so callers see consistent values.
 */

import { AbiCoder, Interface, FunctionFragment, type ParamType } from 'ethers';
import type { AbiParam } from '@spectrum-nodes/api-spec/models';
import { ValidationError } from '../types/errors.js';

/** Internal normalized ABI function — api-spec's `ContractAbiFunction` has optional inputs/outputs. */
export interface AbiFunction {
  name: string;
  type?: 'function' | 'constructor' | 'receive' | 'fallback';
  inputs: AbiParam[];
  outputs: AbiParam[];
  stateMutability?: string;
}

// --- Helpers ---

/** Build canonical Solidity type string (tuple → (address,uint256)). */
function canonicalType(param: AbiParam): string {
  if (param.type === 'tuple' && param.components) {
    return `(${param.components.map((c) => canonicalType(c)).join(',')})`;
  }
  if (param.type.startsWith('tuple[') && param.components) {
    const suffix = param.type.slice(5);
    return `(${param.components.map((c) => canonicalType(c)).join(',')})${suffix}`;
  }
  return param.type;
}

/** Convert AbiParam to ethers-compatible JSON ABI component. */
function toJsonComponent(param: AbiParam): Record<string, unknown> {
  const entry: Record<string, unknown> = {
    type: param.type,
    name: param.name ?? '',
  };
  if (param.components) {
    entry.components = param.components.map(toJsonComponent);
  }
  return entry;
}

/** Convert AbiFunction to ethers-compatible JSON ABI entry. */
function toJsonAbi(fn: AbiFunction): Record<string, unknown> {
  return {
    type: fn.type ?? 'function',
    name: fn.name,
    inputs: fn.inputs.map(toJsonComponent),
    outputs: fn.outputs.map(toJsonComponent),
    stateMutability: fn.stateMutability ?? 'nonpayable',
  };
}

// --- Encoder ---

/** ABI-encode a function call (0x + selector + encoded args). */
export function encodeCall(fn: AbiFunction, args: unknown[]): string {
  const iface = new Interface([toJsonAbi(fn)]);
  return iface.encodeFunctionData(fn.name, args);
}

// --- Decoder ---

/** Normalize ethers Result values to plain JS types. */
function normalizeDecoded(value: unknown, param: AbiParam): unknown {
  /* v8 ignore start -- defensive nullish + rare tuple-as-bare-array path */
  if (value === null || value === undefined) {
    return value;
  }
  /* v8 ignore stop */

  // Addresses → lowercase for consistency
  if (param.type === 'address' && typeof value === 'string') {
    return value.toLowerCase();
  }

  // Arrays
  if (Array.isArray(value)) {
    const arrayMatch = param.type.match(/^(.+?)(\[\d*\])$/);
    if (arrayMatch) {
      const baseParam: AbiParam = { type: arrayMatch[1], components: param.components };
      return value.map((v: unknown) => normalizeDecoded(v, baseParam));
    }
    // Tuple → plain array with normalized components
    /* v8 ignore start -- ethers usually surfaces tuples via Result/arrayMatch paths */
    if (param.type === 'tuple' && param.components) {
      return param.components.map((c, i) => normalizeDecoded(value[i], c));
    }
    /* v8 ignore stop */
  }

  return value;
}

/** Decode ABI-encoded return data into an array of values. */
export function decodeResult(outputs: AbiParam[], data: string): unknown[] {
  const hex = data.startsWith('0x') ? data : `0x${data}`;
  if (hex.length <= 2) {
    return [];
  }

  const types = outputs.map((o) => canonicalType(o));
  const coder = AbiCoder.defaultAbiCoder();
  const result = coder.decode(types, hex);

  return outputs.map((output, i) => normalizeDecoded(result[i], output));
}

// --- Human-readable ABI parser ---

/** Convert ethers ParamType back to our AbiParam format. */
function fromParamType(pt: ParamType): AbiParam {
  const result: AbiParam = { type: '' };

  if (pt.arrayChildren) {
    // Array type (dynamic or fixed)
    const child = fromParamType(pt.arrayChildren);
    const suffix = pt.arrayLength === -1 ? '[]' : `[${pt.arrayLength}]`;
    if (child.type === 'tuple' && child.components) {
      result.type = `tuple${suffix}`;
      result.components = child.components;
    } else {
      result.type = child.type + suffix;
    }
  } else if (pt.components) {
    // Tuple type
    result.type = 'tuple';
    result.components = pt.components.map((c) => fromParamType(c));
  } else {
    // Primitive type
    result.type = pt.type;
  }

  if (pt.name) {
    result.name = pt.name;
  }

  return result;
}

/**
 * Parse a human-readable ABI string into an AbiFunction.
 *
 * @example
 * parseHumanReadableAbi('function balanceOf(address owner) view returns (uint256)')
 */
export function parseHumanReadableAbi(sig: string): AbiFunction {
  const trimmed = sig.trim();
  if (!trimmed.match(/^function\s+/)) {
    throw new ValidationError('abi', `Cannot parse ABI signature: "${sig}"`);
  }

  let fragment: FunctionFragment;
  try {
    fragment = FunctionFragment.from(trimmed);
  } catch {
    throw new ValidationError('abi', `Cannot parse ABI signature: "${sig}"`);
  }

  const inputs = fragment.inputs.map((p) => fromParamType(p));
  const outputs = fragment.outputs.map((p) => fromParamType(p));

  return {
    name: fragment.name,
    type: 'function',
    inputs,
    outputs,
    stateMutability: fragment.stateMutability,
  };
}
